"use client";

import type { StoreCardData } from "@/types/store";
import Link from "next/link";
import Image from "next/image";
import { getStoreLogoAlt } from "@/lib/storeLogo";

interface ButtonGridProps {
  stores: StoreCardData[];
}

function ButtonGrid({ stores }: ButtonGridProps) {
  if (!stores.length) {
    return <p className="text-center text-gray-500">No stores available.</p>;
  }

  const getImageUrl = (image: string | undefined) => {
    if (!image) return "/placeholder-store.png";
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    return `https://www.itscoupons.com${image.startsWith("/") ? image : `/${image}`}`;
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-y-6 gap-x-3 sm:gap-x-5 justify-items-center w-full">
      {stores.map((store) => (
        <Link
          href={`/stores/${store.slug}`}
          key={store._id}
          title={store.name}
          className="group flex w-full max-w-[180px] items-center gap-2 rounded-[14px] border border-gray-300 bg-white px-3 py-3 shadow-sm transition hover:bg-gray-50"
        >
          <span className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-gray-50">
            <Image
              src={getImageUrl(store.image)}
              alt={getStoreLogoAlt(store.slug)}
              width={28}
              height={28}
              sizes="28px"
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.src = "/placeholder-store.png";
              }}
            />
          </span>
          <span className="truncate text-sm font-medium text-gray-800 sm:text-[15px]">
            {store.name}
          </span>
        </Link>
      ))}
    </div>
  );
}

interface StoresComponentProps {
  recentlyUpdatedStores: StoreCardData[];
  popularStores: StoreCardData[];
}

export default function StoresComponent({
  recentlyUpdatedStores,
  popularStores,
}: StoresComponentProps) {
  return (
    <div className="max-w-6xl mx-auto py-3 sm:py-5 px-4 sm:px-6 lg:px-8">
      {/* Recently Updated Section */}
      <section className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-purple-700 text-center mb-3">
          Stores Recently Updated
        </h2>

        <div className="flex justify-end mb-5">
          <a href="/stores" aria-label="View all stores" className="text-sm sm:text-base text-purple-700 hover:underline">
            VIEW ALL
          </a>
        </div>

        <ButtonGrid stores={recentlyUpdatedStores} />
      </section>

      {/* Popular Stores Section */}
      <section>
        <h2 className="text-2xl sm:text-3xl font-bold text-purple-700 text-center mb-3">
          Popular Stores
        </h2>

        <div className="flex justify-end mb-5">
          <a href="/stores" aria-label="View all stores" className="text-sm sm:text-base text-purple-700 hover:underline">
            VIEW ALL
          </a>
        </div>

        <ButtonGrid stores={popularStores} />
      </section>
    </div>
  );
}
